/**
 * Tool Result Summary Generator
 *
 * Generates human-readable summaries from completed tool call results.
 * Used as a UI fallback when the model fails to write a text response.
 */

type ToolOutput = Record<string, unknown>;
type ToolInput = Record<string, unknown>;

interface ToolCallInfo {
  toolName: string;
  input?: ToolInput;
  finalOutput?: ToolOutput;
}

/**
 * Summary generators for action tools that produce meaningful results.
 * Each returns a sentence summarising what happened, using data from the result.
 */
const summaryGenerators: Record<string, (input: ToolInput, output: ToolOutput) => string> = {
  createFloat: (_input, output) => {
    const float = output.float as Record<string, unknown> | undefined;
    const name = float?.name || float?.heading || (_input.title as string) || "Untitled";
    const candidateCount = output.candidate_count || (float?.candidates as unknown[] | undefined)?.length || 0;
    const qualityScore = float?.quality_score;
    const shareLink = float?.share_link;
    const urls = output.urls as Record<string, string> | undefined;
    const editUrl = urls?.edit;

    let summary = `Created float **"${name}"** with ${candidateCount} candidate${candidateCount !== 1 ? "s" : ""}`;
    if (qualityScore) summary += ` (quality score: ${qualityScore})`;
    summary += `. Status: ${float?.status || "draft"}.`;
    if (shareLink) summary += ` [Share link](${shareLink})`;
    if (editUrl) summary += ` · [Edit](${editUrl})`;
    return summary;
  },

  createCandidate: (_input, output) => {
    const candidate = output.candidate as Record<string, unknown> | undefined;
    const name = candidate?.name || (_input.name as string) || "Unknown";
    const title = candidate?.position_title || candidate?.title || "";
    let summary = `Created candidate **${name}**`;
    if (title) summary += ` — ${title}`;
    summary += ".";
    return summary;
  },

  createContact: (_input, output) => {
    const contact = output.contact as Record<string, unknown> | undefined;
    const name = contact?.full_name || contact?.name ||
      `${(_input.firstName as string) || ""} ${(_input.lastName as string) || ""}`.trim() || "Unknown";
    const company = contact?.company || (_input.company as string) || "";
    let summary = `Created contact **${name}**`;
    if (company) summary += ` at ${company}`;
    summary += ".";
    return summary;
  },

  createFloatEmail: (_input, output) => {
    const email = output.email as Record<string, unknown> | undefined;
    const subject = email?.subject || (_input.subject as string) || "";
    let summary = "Email draft created";
    if (subject) summary += `: "${subject}"`;
    summary += ".";
    return summary;
  },

  sendTestEmail: (_input, output) => {
    const message = output.message as string | undefined;
    return message || "Test email sent successfully.";
  },

  sendEmailCampaign: (_input, output) => {
    const message = output.message as string | undefined;
    return message || "Email campaign sent.";
  },

  createShareLink: (_input, output) => {
    const url = output.url || output.share_link || output.link;
    if (url) return `Share link created: ${url}`;
    return "Share link created.";
  },

  updateFloat: (_input, output) => {
    const float = output.float as Record<string, unknown> | undefined;
    const name = float?.name || "the float";
    return `Updated **${name}**.`;
  },

  deleteFloat: () => "Float deleted.",
  deleteCandidate: () => "Candidate deleted.",
  deleteContact: () => "Contact deleted.",

  duplicateFloat: (_input, output) => {
    const float = output.float as Record<string, unknown> | undefined;
    const name = float?.name || "float";
    return `Duplicated float — new copy: **${name}**.`;
  },

  searchCandidates: (_input, output) => {
    const candidates = output.candidates as unknown[] | undefined;
    const count = candidates?.length ?? (output.total as number | undefined) ?? 0;
    if (count === 0) return "No candidates found matching your criteria.";
    return `Found **${count}** candidate${count !== 1 ? "s" : ""} matching your search.`;
  },

  searchContacts: (_input, output) => {
    const contacts = output.contacts as unknown[] | undefined;
    const count = contacts?.length ?? (output.total as number | undefined) ?? 0;
    if (count === 0) return "No contacts found matching your criteria.";
    return `Found **${count}** contact${count !== 1 ? "s" : ""}.`;
  },

  searchFloats: (_input, output) => {
    const floats = output.floats as unknown[] | undefined;
    const count = floats?.length ?? (output.total as number | undefined) ?? 0;
    if (count === 0) return "No floats found matching your criteria.";
    return `Found **${count}** float${count !== 1 ? "s" : ""}.`;
  },

  queryData: (_input, output) => {
    const data = output.data as unknown[] | undefined;
    const count = data?.length ?? 0;
    const explanation = _input.explanation as string | undefined;
    if (count === 0) return explanation
      ? `Query returned no results for: ${explanation}`
      : "Query returned no results.";
    return explanation
      ? `Retrieved **${count}** result${count !== 1 ? "s" : ""}. ${explanation}`
      : `Retrieved **${count}** result${count !== 1 ? "s" : ""}.`;
  },

  searchWeb: (_input, output) => {
    const results = output.results as unknown[] | undefined;
    const count = results?.length ?? 0;
    const query = _input.query as string | undefined;
    if (count === 0) return query
      ? `No web results found for "${query}".`
      : "No web results found.";
    return query
      ? `Found **${count}** web result${count !== 1 ? "s" : ""} for "${query}".`
      : `Found **${count}** web result${count !== 1 ? "s" : ""}.`;
  },
};

/**
 * Generate a summary for a single completed tool call.
 */
function getToolCallSummary(toolCall: ToolCallInfo): string | null {
  const { toolName, input, finalOutput } = toolCall;

  // Skip utility tools that don't need summarising
  if (toolName === "suggestFollowUps" || toolName === "judgeConversation") {
    return null;
  }

  const generator = summaryGenerators[toolName];
  if (generator && finalOutput) {
    try {
      return generator((input as ToolInput) ?? {}, finalOutput as ToolOutput);
    } catch {
      // Fall through to generic
    }
  }

  // Generic fallback using the tool's message field if present
  if (finalOutput) {
    const output = finalOutput as ToolOutput;
    if (typeof output.message === "string") return output.message;
    if (output.status === "success" && typeof output.data === "object") {
      return `${toolName} completed successfully.`;
    }
  }

  return null;
}

/**
 * Generate a combined summary from all completed tool calls in a message.
 * Returns null if no meaningful summary can be generated.
 */
export function generateToolResultSummary(toolCalls: ToolCallInfo[]): string | null {
  const summaries = toolCalls
    .map(getToolCallSummary)
    .filter((s): s is string => s !== null);

  if (summaries.length === 0) return null;
  return summaries.join("\n\n");
}
